"use client"

import { useState, useEffect, useCallback } from "react"
import Link from "next/link"
import Image from "next/image"

type Props = {
  slug: string
  name: string
  description: string
  beforeCount: number
  afterCount: number
}

type GalleryImage = {
  src: string
  alt: string
}

function generateImageList(slug: string, type: "before" | "after", count: number): GalleryImage[] {
  return Array.from({ length: count }, (_, i) => ({
    src: `/img/gallery/${slug}/${type}-${i + 1}.jpg`,
    alt: `${type === "before" ? "Before" : "After"} photo ${i + 1}`,
  }))
}

export default function GalleryDetail({ slug, name, description, beforeCount, afterCount }: Props) {
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [lightboxImages, setLightboxImages] = useState<GalleryImage[]>([])

  const beforeImages = generateImageList(slug, "before", beforeCount)
  const afterImages = generateImageList(slug, "after", afterCount)

  function openLightbox(images: GalleryImage[], index: number) {
    setLightboxImages(images)
    setLightboxIndex(index)
    setLightboxOpen(true)
  }

  function closeLightbox() {
    setLightboxOpen(false)
  }

  const nextImage = useCallback(() => {
    setLightboxIndex((prev) => (prev + 1) % lightboxImages.length)
  }, [lightboxImages.length])

  const prevImage = useCallback(() => {
    setLightboxIndex((prev) => (prev - 1 + lightboxImages.length) % lightboxImages.length)
  }, [lightboxImages.length])

  useEffect(() => {
    if (!lightboxOpen) {
      return
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        closeLightbox()
      } else if (e.key === "ArrowRight") {
        nextImage()
      } else if (e.key === "ArrowLeft") {
        prevImage()
      }
    }

    document.body.style.overflow = "hidden"
    window.addEventListener("keydown", handleKeyDown)

    return () => {
      document.body.style.overflow = ""
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [lightboxOpen, nextImage, prevImage])

  return (
    <>
      <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-12 lg:py-16">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <h1 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
            {name}
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-gray-300">
            {description}
          </p>
          <p className="mt-2 text-sm text-gray-400">
            This vehicle is for viewing only and is not for sale.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-6 lg:px-8">
        <Link
          href="/gallery"
          className="inline-flex items-center gap-2 rounded-lg border-2 border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 hover:border-gray-400 transition-colors"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
          </svg>
          Back to Gallery
        </Link>
      </div>

      {beforeCount > 0 && (
        <section className="bg-gray-200 py-8 lg:py-12">
          <div className="mx-auto max-w-7xl px-4 lg:px-8">
            <h2 className="mb-6 text-xl font-bold text-gray-700">Before</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {beforeImages.map((image, index) => (
                <button
                  key={image.src}
                  onClick={() => openLightbox(beforeImages, index)}
                  className="group relative overflow-hidden rounded-lg border-4 border-gray-400 bg-gray-300 aspect-[4/3] cursor-pointer"
                >
                  <Image
                    src={image.src}
                    alt={image.alt}
                    fill
                    className="object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/90 text-gray-900">
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM10.5 7.5v6m3-3h-6" />
                      </svg>
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {afterCount > 0 && (
        <section className="bg-gradient-to-br from-amber-50 to-amber-100 py-8 lg:py-12">
          <div className="mx-auto max-w-7xl px-4 lg:px-8">
            <h2 className="mb-6 text-xl font-bold text-amber-800">After</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {afterImages.map((image, index) => (
                <button
                  key={image.src}
                  onClick={() => openLightbox(afterImages, index)}
                  className="group relative overflow-hidden rounded-xl border-4 border-amber-300 bg-amber-200 aspect-[4/3] cursor-pointer shadow-lg"
                >
                  <Image
                    src={image.src}
                    alt={image.alt}
                    fill
                    className="object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/90 text-gray-900">
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM10.5 7.5v6m3-3h-6" />
                      </svg>
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="border-t border-gray-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-12 lg:px-8 lg:py-16">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Have a Restoration to Share?</h2>
            <p className="mt-2 text-gray-600">
              We&apos;d love to see your before and after photos! Share your restoration project with us.
            </p>
            <Link
              href="/contact-information"
              className="mt-6 inline-flex items-center gap-2 rounded-lg bg-amber-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-amber-600 transition-colors"
            >
              Contact Us
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {lightboxOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95">
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            aria-label="Close lightbox"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {lightboxImages.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-4 z-10 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
                aria-label="Previous image"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                </svg>
              </button>
              <button
                onClick={nextImage}
                className="absolute right-4 z-10 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
                aria-label="Next image"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
              </button>
            </>
          )}

          <div className="relative max-h-[85vh] max-w-[90vw]">
            <Image
              src={lightboxImages[lightboxIndex]?.src || ""}
              alt={lightboxImages[lightboxIndex]?.alt || ""}
              width={1200}
              height={900}
              className="max-h-[85vh] w-auto object-contain"
            />
          </div>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-sm">
            {lightboxIndex + 1} / {lightboxImages.length}
          </div>
        </div>
      )}
    </>
  )
}
